<template>
    <div class="ckeditor-compat" v-bind="$attrs">
        <div ref="element"></div>
    </div>
</template>

<script>
const READ_ONLY_LOCK_ID = "noshow-ckeditor-readonly";

export default {
    name: "CkeditorCompat",
    inheritAttrs: false,
    props: {
        config: {
            type: Object,
            default: () => ({}),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        editor: {
            type: [Function, Object],
            required: true,
        },
        modelValue: {
            type: String,
            default: undefined,
        },
        value: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isApplyingExternalValue: false,
            $_instance: null,
        };
    },
    computed: {
        currentValue() {
            return this.modelValue !== undefined ? this.modelValue : this.value;
        },
    },
    async mounted() {
        await this.createEditor();
    },
    beforeUnmount() {
        this.destroyEditor();
    },
    methods: {
        async createEditor() {
            if (typeof this.editor?.create !== "function") {
                return;
            }

            let instance = null;

            try {
                instance = await this.editor.create(this.$refs.element, this.config || {});
            } catch (error) {
                console.error(error);
                this.$emit("error", error);
                return;
            }

            this.$_instance = instance;
            this.$emit("ready", instance);

            const initialValue = this.currentValue || "";
            if (instance.getData() !== initialValue) {
                instance.setData(initialValue);
            }

            this.applyDisabledState(this.disabled);

            instance.model.document.on("change:data", () => {
                if (this.isApplyingExternalValue) {
                    return;
                }

                const data = instance.getData();
                this.$emit("input", data);
                this.$emit("update:modelValue", data);
            });
        },
        applyDisabledState(isDisabled) {
            if (!this.$_instance) {
                return;
            }

            if (typeof this.$_instance.enableReadOnlyMode === "function" && typeof this.$_instance.disableReadOnlyMode === "function") {
                if (isDisabled) {
                    this.$_instance.enableReadOnlyMode(READ_ONLY_LOCK_ID);
                } else {
                    this.$_instance.disableReadOnlyMode(READ_ONLY_LOCK_ID);
                }
                return;
            }

            this.$_instance.isReadOnly = isDisabled;
        },
        async syncExternalValue(nextValue) {
            if (!this.$_instance) {
                return;
            }

            const normalizedValue = nextValue || "";
            if (this.$_instance.getData() === normalizedValue) {
                return;
            }

            this.isApplyingExternalValue = true;

            try {
                await this.$_instance.setData(normalizedValue);
            } finally {
                this.isApplyingExternalValue = false;
            }
        },
        destroyEditor() {
            if (!this.$_instance) {
                return;
            }

            const instance = this.$_instance;
            this.$_instance = null;
            instance.destroy();
        },
    },
    watch: {
        currentValue(nextValue) {
            this.syncExternalValue(nextValue);
        },
        disabled(nextValue) {
            this.applyDisabledState(nextValue);
        },
    },
};
</script>

<style>
.ckeditor-compat .ck.ck-editor__editable_inline {
    min-height: 16rem;
}
</style>
